import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download, LogIn, BookOpen, MousePointerClick, Mail, MessageSquare, ShieldCheck, Search } from 'lucide-react';
import { Panel } from '../programme/insights/HeadlineCards';
import { API, fmtDate, downloadXlsx } from './AnalyticsBits';

const TYPES = [
  { id: 'login', label: 'Logins', icon: LogIn, tone: 'text-sky-300 bg-sky-500/10' },
  { id: 'learning', label: 'Learning', icon: BookOpen, tone: 'text-emerald-300 bg-emerald-500/10' },
  { id: 'page', label: 'Pages & lessons', icon: MousePointerClick, tone: 'text-zinc-300 bg-zinc-500/10' },
  { id: 'email', label: 'Emails', icon: Mail, tone: 'text-amber-300 bg-amber-500/10' },
  { id: 'communication', label: 'Messages', icon: MessageSquare, tone: 'text-violet-300 bg-violet-500/10' },
  { id: 'admin', label: 'Admin actions', icon: ShieldCheck, tone: 'text-rose-300 bg-rose-500/10' },
];
const TYPE = Object.fromEntries(TYPES.map((t) => [t.id, t]));
const PAGE = 100;
const inp = 'h-9 bg-zinc-950 border border-zinc-700 rounded-md text-sm text-white px-2 focus:outline-none focus:border-navigate-gold';

export default function UserAuditLog({ userId, token, name }) {
  const [cats, setCats] = useState([]);
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const [q, setQ] = useState('');
  const [data, setData] = useState({ events: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const countRef = useRef(0);

  const params = useCallback((offset = 0) => ({ category: cats.join(','), ...range, q, offset, limit: PAGE }), [cats, range, q]);
  const load = useCallback((append = false) => {
    setLoading(true);
    axios.get(`${API}/admin/analytics/users/${userId}/audit`, { headers: { Authorization: `Bearer ${token}` }, params: params(append ? countRef.current : 0) })
      .then((r) => setData((d) => {
        const events = append ? [...d.events, ...r.data.events] : r.data.events;
        countRef.current = events.length;
        return { ...r.data, events };
      }))
      .catch(() => { countRef.current = 0; setData({ events: [], total: 0, counts: {} }); })
      .finally(() => setLoading(false));
  }, [userId, token, params]);
  useEffect(() => { const t = setTimeout(() => load(false), 250); return () => clearTimeout(t); }, [load]);

  const toggle = (id) => setCats((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const exportUrl = `${API}/admin/analytics/users/${userId}/audit/export?${new URLSearchParams({ category: cats.join(','), ...range, q })}`;

  return (
    <Panel title="Audit log" subtitle="Everything this user has done on the platform, and everything done to their account" testId="user-audit-log"
      aside={<button onClick={() => downloadXlsx(exportUrl, token, `${name}_audit_log.xlsx`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs" data-testid="export-audit-log-btn"><Download className="h-3.5 w-3.5" />Export</button>}>
      <div className="flex flex-wrap gap-2 mb-3">
        {TYPES.map(({ id, label, icon: Icon }) => {
          const on = cats.includes(id);
          return (
            <button key={id} onClick={() => toggle(id)} data-testid={`audit-filter-${id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-navigate-gold text-black border-navigate-gold' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
              <Icon className="h-3.5 w-3.5" />{label}<span className="opacity-60">{data.counts?.[id] ?? 0}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input className={`${inp} w-full pl-8`} placeholder="Search activity..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" />
        </div>
        <input type="date" className={inp} value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="audit-date-from" />
        <span className="text-zinc-500 text-xs">to</span>
        <input type="date" className={inp} value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="audit-date-to" />
      </div>
      <p className="text-xs text-zinc-500 mb-2" data-testid="audit-total">{loading && !data.events.length ? 'Loading...' : `${data.total} event(s)`}</p>
      <ol className="divide-y divide-zinc-800 border border-zinc-800 rounded-lg max-h-[560px] overflow-y-auto" data-testid="audit-events">
        {data.events.map((e, i) => {
          const t = TYPE[e.category] || TYPE.page;
          const Icon = t.icon;
          return (
            <li key={`${e.at}-${i}`} className="flex gap-3 px-4 py-3" data-testid={`audit-event-${i}`}>
              <span className={`h-7 w-7 rounded-full grid place-items-center shrink-0 ${t.tone}`}><Icon className="h-3.5 w-3.5" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white">{e.title}</p>
                {(e.detail || e.actor) && <p className="text-xs text-zinc-500">{e.detail}{e.detail && e.actor ? ' · ' : ''}{e.actor ? `by ${e.actor}` : ''}</p>}
              </div>
              <time className="text-xs text-zinc-500 whitespace-nowrap">{fmtDate(e.at, true)}</time>
            </li>
          );
        })}
        {!loading && !data.events.length && <li className="px-4 py-8 text-center text-sm text-zinc-500">No activity matches these filters.</li>}
      </ol>
      {data.events.length < data.total && (
        <button onClick={() => load(true)} disabled={loading} className="mt-3 text-xs text-navigate-gold hover:underline disabled:opacity-50" data-testid="audit-load-more">
          {loading ? 'Loading...' : `Show more (${data.total - data.events.length} left)`}
        </button>
      )}
    </Panel>
  );
}
